import axios from 'axios'
import { env } from '../../config/env'
import { useAuthStore } from '../auth/store'
import { logger } from '../utils/logger'

// Единый формат ошибки с бэкенда
export type ErrorEnvelope = {
  code: string
  message: string
  details?: Record<string, unknown>
  trace_id: string
  timestamp: string
}

export const http = axios.create({ 
  baseURL: env.resolveApiUrl(),
  timeout: 30000, // 30 секунд тайм-аут
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor для добавления токена авторизации
http.interceptors.request.use(async (config) => {
  // Для FormData убираем Content-Type, чтобы браузер сам установил с boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  const isDev = env.isDevelopment
  
  // 🔥 ИСПРАВЛЕНИЕ: Получаем токен БЕЗ автоматического обновления в interceptor
  let token: string | null = null
  let storeToken: string | null = null
  try {
    const authStore = useAuthStore.getState()
    
    // КРИТИЧНО: НЕ обновляем токен в interceptor для избежания циклов!
    // Обновление токена должно происходить только при получении 401 ошибки
    // или по инициативе пользователя, НЕ перед каждым запросом
    
    // Просто получаем текущий токен из store
    storeToken = authStore.token
    
    // Логируем только если токен истек (для диагностики)
    if (authStore.isTokenExpired() && storeToken) {
      logger.warn('⚠️ HTTP Interceptor: Токен истек, но НЕ обновляем автоматически (избегаем циклов)')
    }
    
    // 🔥 ИСПРАВЛЕНИЕ: Проверяем все токены на истечение
    const lsToken = localStorage.getItem('alfastil_token')
    const webToken = localStorage.getItem('alfastil_web_token')
    
    // Функция проверки токена
    const isTokenValid = (tokenToCheck: string | null): boolean => {
      if (!tokenToCheck) return false
      try {
        const payload = JSON.parse(atob(tokenToCheck.split('.')[1]))
        const now = Math.floor(Date.now() / 1000)
        return payload.exp && payload.exp >= now
      } catch {
        return false
      }
    }
    
    // Выбираем первый валидный токен
    if (isTokenValid(storeToken)) {
      token = storeToken
    } else if (isTokenValid(lsToken)) {
      token = lsToken
    } else if (isTokenValid(webToken)) {
      token = webToken
    } else {
      token = null
      // Очищаем все истекшие токены
      if (lsToken && !isTokenValid(lsToken)) {
        localStorage.removeItem('alfastil_token')
      }
      if (webToken && !isTokenValid(webToken)) {
        localStorage.removeItem('alfastil_web_token')
      }
    }
    
    // Debug информация о токенах (только для разработки)
    logger.debug('🔍 HTTP Interceptor tokens:', {
      store: storeToken?.substring(0, 20),
      localStorage: localStorage.getItem('alfastil_token')?.substring(0, 20),
      final: token?.substring(0, 20)
    })
  } catch {
    // Fallback если store недоступен
    token = localStorage.getItem('alfastil_token')
  }
  
  if (token) {
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${token}`
    logger.debug('🔐 HTTP Interceptor: добавляем токен', token.substring(0, 20) + '...')
    logger.debug('🔐 HTTP Interceptor: URL запроса:', config.url)
  } else {
    logger.warn('⚠️ HTTP Interceptor: токен НЕ найден! URL:', config.url)
    logger.warn('⚠️ localStorage tokens:', {
      alfastil_token: localStorage.getItem('alfastil_token')?.substring(0, 20),
      alfastil_web_token: localStorage.getItem('alfastil_web_token')?.substring(0, 20)
    })
  }
  
  // Добавляем telegram_id ТОЛЬКО в Telegram режиме
  try {
    const mode = useAuthStore.getState().mode
    const telegramId = localStorage.getItem('telegram_id')
    if (mode === 'telegram' && telegramId) {
      config.headers['X-Telegram-Id'] = telegramId
    }
  } catch {
    // ignore
  }
  
  // Определяем тип клиента для аналитики: берем из auth store
  try {
    const detected = useAuthStore.getState().mode
    config.headers['X-Client-Type'] = detected || 'unknown'
  } catch {
    const fallback = localStorage.getItem('alfastil_web_token') ? 'browser' : 'unknown'
    config.headers['X-Client-Type'] = fallback
  }
  
  return config
})

// Interceptor для обработки ответов и ошибок
http.interceptors.response.use(
  async (response) => {
    // При успешном ответе завершаем проверку доступа для Telegram пользователей
    try {
      const { useAuthStore } = await import('../auth/store')
      const store = useAuthStore.getState()
      if (store.isCheckingAccess) {
        store.setCheckingAccess(false)
        logger.log('✅ Проверка доступа завершена успешно')
      }
    } catch (error) {
      // Игнорируем ошибки при обновлении состояния
    }
    return response
  },
  async (error) => {
    const isDev = env.isDevelopment
    const resp = error?.response
    
    // Приводим к ErrorEnvelope, если сервер вернул произвольный формат
    const fallback: ErrorEnvelope = {
      code: String(resp?.status ?? 'internal_error'),
      message: resp?.data?.message || resp?.statusText || 'Ошибка запроса',
      details: resp?.data?.details || undefined,
      trace_id: resp?.data?.trace_id || 'n/a',
      timestamp: new Date().toISOString(),
    }
    
    const err: ErrorEnvelope = resp?.data?.code ? resp.data : fallback

    // Обработка 401 ошибки (неавторизован)
    if (resp?.status === 401) {
      const authError = resp.headers?.['x-auth-error']
      const currentUrl = error.config?.url || 'unknown'
      logger.warn(`🚫 Ошибка авторизации (401) для ${currentUrl}, тип: ${authError || 'unknown'}`)
      
      // Очищаем токены при невалидных токенах и отозванном доступе
      const shouldClearTokens = authError === 'invalid_token' || authError === 'token_expired' || authError === 'user_not_found' || authError === 'access_denied'
      
      if (shouldClearTokens) {
        logger.warn('🚫 Токен недействителен, очищаем авторизацию')
        try {
          const { useAuthStore } = await import('../auth/store')
          const store = useAuthStore.getState()
          
          // КРИТИЧНО: Для Telegram режима при истечении токена пытаемся переавторизоваться
          if (store.mode === 'telegram' && (authError === 'token_expired' || authError === 'invalid_token')) {
            logger.log('🔄 Telegram режим: Пытаемся переавторизоваться с сохраненными initData')
            
            // Пытаемся переавторизоваться через authService
            try {
              const { authService } = await import('../auth/authService')
              const success = await authService.initializeTelegramAuth()
              
              if (success) {
                logger.log('✅ Переавторизация в Telegram режиме успешна')
                // Повторяем оригинальный запрос с новым токеном
                const newToken = useAuthStore.getState().token
                if (newToken && error.config) {
                  error.config.headers['Authorization'] = `Bearer ${newToken}`
                  return http.request(error.config)
                }
              } else {
                logger.warn('❌ Переавторизация в Telegram режиме не удалась')
                store.clear()
              }
            } catch (reAuthError) {
              logger.error('❌ Ошибка при переавторизации в Telegram режиме:', reAuthError)
              store.clear()
            }
          } else {
            // Для браузерного режима или других ошибок - просто очищаем
            store.clear()
          }
          
          // Устанавливаем специальное сообщение для access_denied ПОСЛЕ очистки
          if (authError === 'access_denied') {
            store.setError('access_denied')
            logger.warn('🚫 Доступ заблокирован - устанавливаем error: access_denied')
          }
          
          // Завершаем проверку доступа
          store.setCheckingAccess(false)
        } catch (storeError) {
          logger.error('Ошибка при очистке auth store:', storeError)
          // Fallback - очищаем напрямую
          localStorage.removeItem('alfastil_token')
          localStorage.removeItem('alfastil_user')
          // НЕ очищаем telegram_id - это информация о способе входа, не об авторизации
          // localStorage.removeItem('telegram_id')
          localStorage.removeItem('alfastil_web_token')
        }
      } else {
        // Временная проблема - НЕ очищаем токены
        logger.warn('⚠️ Временная проблема авторизации, токены сохраняем')
      }
    }

    // Обработка 429 ошибки (слишком много запросов)
    if (resp?.status === 429) {
      logger.warn('⚠️ Превышен лимит запросов (429)')
      // Можно добавить автоматический retry с задержкой
    }

    // Обработка 5xx ошибок (серверные ошибки)
    if (resp?.status >= 500) {
      logger.error('💥 Серверная ошибка:', resp?.status, err.message)
      // Можно добавить автоматический retry для серверных ошибок
    }

    return Promise.reject(err)
  }
)
